import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Wrench, AlertTriangle, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const FAULT_OPTIONS = [
  { value: 'worker_fault', label: 'Вина сотрудника', desc: 'Работник исправляет бесплатно', icon: User, color: 'border-red-500/40 bg-red-500/10' },
  { value: 'company_fault', label: 'Вина компании', desc: 'Оплачивается работнику по часам', icon: Building2, color: 'border-blue-500/40 bg-blue-500/10' },
];

const DURATION_PRESETS = [30, 60, 90, 120, 180];

function formatDuration(m) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min}мин`;
  if (min === 0) return `${h}ч`;
  return `${h}ч ${min}мин`;
}

export function CreateServiceDialog({ order, open, onOpenChange, onCreated }) {
  const { api } = useAuth();
  const [reason, setReason] = useState('');
  const [faultType, setFaultType] = useState('worker_fault');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!reason.trim()) {
      toast.error('Укажите сервисное задание');
      return;
    }
    if (reason.trim().length < 15) {
      toast.warning('Опишите задачу более подробно (минимум 15 символов)');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post(`/orders/${order.id}/create-service`, {
        reason: reason.trim(),
        fault_type: faultType,
        duration_minutes: durationMinutes,
      });
      toast.success(`Сервис создан${res.data.is_payable ? ' (оплачиваемый)' : ' (бесплатно)'}`);
      setReason('');
      setFaultType('worker_fault');
      setDurationMinutes(60);
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка создания сервиса');
    } finally {
      setLoading(false);
    }
  };

  const selectedFault = FAULT_OPTIONS.find(f => f.value === faultType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="create-service-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-amber-500" />
            Создать сервис
          </DialogTitle>
          <DialogDescription>
            Повторный выезд для исправления: {order?.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Fault type selection */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Виновник</Label>
            <div className="grid grid-cols-2 gap-2">
              {FAULT_OPTIONS.map(opt => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setFaultType(opt.value)}
                    className={cn(
                      "p-3 rounded-lg border-2 text-left transition-all",
                      faultType === opt.value ? opt.color : "border-border hover:border-muted-foreground/30"
                    )}
                    data-testid={`fault-${opt.value}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{opt.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reason / Task description */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Сервисное задание</Label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Опишите подробно что нужно исправить..."
              className="min-h-[80px] text-sm"
              data-testid="service-reason"
            />
            <p className={cn("text-[11px] tabular-nums", reason.trim().length < 15 ? "text-muted-foreground" : "text-green-600")}>
              {reason.trim().length}/15 символов мин.
            </p>
          </div>

          {/* Duration */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Длительность</Label>
            <div className="flex flex-wrap gap-1.5">
              {DURATION_PRESETS.map(d => (
                <Button
                  key={d}
                  variant={durationMinutes === d ? "default" : "outline"}
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setDurationMinutes(d)}
                  data-testid={`duration-${d}`}
                >
                  {formatDuration(d)}
                </Button>
              ))}
            </div>
          </div>

          {/* Summary */}
          {selectedFault && (
            <div className={cn("p-3 rounded-lg border text-sm", selectedFault.color)}>
              {faultType === 'worker_fault' ? (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-600 dark:text-red-400">Вина сотрудника</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Работник исправляет бесплатно. В зарплату не включается.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-600 dark:text-blue-400">Вина компании</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Оплачивается работнику по часовой ставке за {formatDuration(durationMinutes)}.
                      В выручку компании не входит.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
          <Button onClick={handleCreate} disabled={loading || !reason.trim()} data-testid="create-service-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wrench className="h-4 w-4 mr-2" />}
            Создать сервис
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
